import { FORM_TIME_EDGES_SECONDS, type InstallationReport, share } from "@bt/domain/report";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChartFrame } from "../charts/frame";
import { type HeatRow, HeatTable } from "../charts/heat-table";
import { useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { type CoreOutputs, useTRPC } from "../trpc";
import { useView } from "../view";
import { areaLabel, BucketsFigure, Grid, ReportView, Section, Stats } from "./kit";

type Totals = CoreOutputs["usage"]["totals"];

/** The areas with a list, an item and a form, and the steps through them. */
const FORM_AREAS = ["parts", "vehicles", "work_orders", "templates", "orders"] as const;
const STEPS = ["open_list", "open_item", "open_edit", "open_create", "save", "cancel"] as const;

/** What the app can count at all, so "nobody used" lists only what someone could have. */
const POSSIBLE: readonly string[] = [
  ...FORM_AREAS.flatMap((area) => STEPS.map((step) => `${area}.${step}`)),
  "to_order.open_list",
  "settings.open_list",
  "settings.open_item",
  "search.palette",
  "search.search",
  "orders.print",
  "orders.copy",
  "orders.email",
  "labels.open_list",
  "labels.open_item",
];

/** `ui.<area>.<action>` totals by "<area>.<action>", over every form time. */
function byAction(totals: Totals): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of totals) {
    const key = row.metric.slice("ui.".length);
    out.set(key, (out.get(key) ?? 0) + row.total);
  }
  return out;
}

/**
 * Interactions on the Usage page (docs/admin-app/figures.md › Interactions): only from people who
 * share usage statistics, so every figure says how many of the active users that is.
 */
export function InteractionsSection() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { installation, range } = useView();
  const query = useQuery(
    trpc.usage.totals.queryOptions({ name: installation, range, prefix: "ui." }),
  );
  return (
    <Section title={t("adminApp.interactions.title")} description={t("adminApp.interactions.hint")}>
      <ReportView>
        {(report) => (
          <QueryView query={query}>
            {(totals) => <Interactions report={report} totals={totals} />}
          </QueryView>
        )}
      </ReportView>
    </Section>
  );
}

function Interactions({ report, totals }: { report: InstallationReport; totals: Totals }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const coverage = share(report.users?.sharingUsage ?? 0, report.users?.active?.last30 ?? 0);
  const over = t("adminApp.interactions.over", { coverage: format.share(coverage) });
  const counts = byAction(totals);
  const area = areaLabel(t);
  const step = (s: string) => t(`adminApp.actions.${s as "save"}`);

  const funnelRows: HeatRow[] = FORM_AREAS.map((a) => {
    const values = STEPS.map((s) => counts.get(`${a}.${s}`) ?? 0);
    const max = Math.max(1, ...values);
    return {
      key: a,
      label: area(a),
      cells: values.map((v) => ({ share: v / max, text: format.count(v) })),
    };
  });
  const completion = FORM_AREAS.map((a) => {
    const saved = counts.get(`${a}.save`) ?? 0;
    const cancelled = counts.get(`${a}.cancel`) ?? 0;
    return { area: a, saved, cancelled, completion: share(saved, saved + cancelled) };
  });
  const formTimes = (action: "save" | "cancel") => {
    const buckets = FORM_TIME_EDGES_SECONDS.map(() => 0);
    for (const row of totals) {
      if (!row.metric.endsWith(`.${action}`) || row.value === "") continue;
      const i = FORM_TIME_EDGES_SECONDS.indexOf(Number(row.value) as 0);
      if (i >= 0) buckets[i] = (buckets[i] ?? 0) + row.total;
    }
    return buckets;
  };
  const unused = POSSIBLE.filter((key) => !counts.get(key));

  return (
    <div className="space-y-4">
      <Stats
        items={[
          {
            key: "coverage",
            label: t("adminApp.interactions.coverage"),
            value: format.share(coverage),
            detail: t("adminApp.interactions.coverageHint"),
          },
          {
            key: "total",
            label: t("adminApp.interactions.total"),
            value: format.count([...counts.values()].reduce((a, b) => a + b, 0)),
          },
        ]}
      />
      <ChartFrame
        title={t("adminApp.interactions.funnels")}
        description={`${t("adminApp.interactions.funnelsHint")} ${over}`}
        csvName="interaction-funnels"
        columns={[
          { key: "area", label: t("adminApp.interactions.area") },
          ...STEPS.map((s) => ({ key: s, label: step(s), numeric: true })),
        ]}
        rows={FORM_AREAS.map((a) => ({
          area: a,
          ...Object.fromEntries(STEPS.map((s) => [s, counts.get(`${a}.${s}`) ?? 0])),
        }))}
      >
        <HeatTable
          caption={t("adminApp.interactions.funnels")}
          rowHeader={t("adminApp.interactions.area")}
          columns={STEPS.map((s) => ({ key: s, label: step(s) }))}
          rows={funnelRows}
          legend={["0", t("adminApp.interactions.mostInArea")]}
        />
      </ChartFrame>
      <Grid>
        <ChartFrame
          title={t("adminApp.interactions.completion")}
          description={`${t("adminApp.interactions.completionHint")} ${over}`}
          csvName="form-completion"
          columns={[
            { key: "area", label: t("adminApp.interactions.area") },
            { key: "saved", label: step("save"), numeric: true },
            { key: "cancelled", label: step("cancel"), numeric: true },
          ]}
          rows={completion.map((c) => ({ area: c.area, saved: c.saved, cancelled: c.cancelled }))}
        >
          <ul className="space-y-1.5 text-sm">
            {completion.map((c) => (
              <li key={c.area} className="flex justify-between gap-3">
                <span>{area(c.area)}</span>
                <span className="text-muted-foreground tabular">
                  {t("adminApp.interactions.completionValue", {
                    share: format.share(c.completion),
                    saved: format.count(c.saved),
                    cancelled: format.count(c.cancelled),
                  })}
                </span>
              </li>
            ))}
          </ul>
        </ChartFrame>
        <BucketsFigure
          title={t("adminApp.interactions.timeSaved")}
          description={over}
          counts={formTimes("save")}
          edges={FORM_TIME_EDGES_SECONDS}
          formatEdge={(n) => format.duration(n)}
          continuous
          csvName="form-time-saved"
        />
        <BucketsFigure
          title={t("adminApp.interactions.timeCancelled")}
          description={over}
          counts={formTimes("cancel")}
          edges={FORM_TIME_EDGES_SECONDS}
          formatEdge={(n) => format.duration(n)}
          continuous
          csvName="form-time-cancelled"
        />
        <ChartFrame
          title={t("adminApp.interactions.unused", { count: unused.length })}
          description={`${t("adminApp.interactions.unusedHint")} ${over}`}
          csvName="unused-actions"
          columns={[{ key: "action", label: t("adminApp.interactions.action") }]}
          rows={unused.map((action) => ({ action }))}
        >
          <ul className="columns-2 text-sm text-muted-foreground">
            {unused.map((key) => {
              const [a = "", s = ""] = key.split(".");
              return <li key={key}>{`${area(a)} · ${step(s)}`}</li>;
            })}
          </ul>
        </ChartFrame>
      </Grid>
    </div>
  );
}
